import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Stack, Button, Table, TableHead, TableBody, TableRow, TableCell, Chip, Typography,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert,
} from '@mui/material'

import { DEXIE_STORE_NAMES, getStoreCounts, getSyncMeta, getVisitStatusCounts } from '../lib/idbUtils'
import { clearLocalDatabase } from '../../../db/db'

// Stores that are expected to be non-empty once the app has synced at least once
const EXPECT_NON_EMPTY = ['comes_from', 'black_lists']

function IndexedDbHealthPanel({ enabledVisitHost }) {
  const { t } = useTranslation()
  const [storeCounts, setStoreCounts] = useState(null)
  const [syncMeta, setSyncMeta] = useState([])
  const [loading, setLoading] = useState(false)
  const [resetDialogOpen, setResetDialogOpen] = useState(false)
  const [pendingVisitsCount, setPendingVisitsCount] = useState(0)
  const [resetting, setResetting] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const [counts, meta] = await Promise.all([getStoreCounts(), getSyncMeta()])
      setStoreCounts(counts)
      setSyncMeta(meta)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const openResetDialog = useCallback(async () => {
    const visitCounts = await getVisitStatusCounts()
    // Both statuses get moved into quarantined_visits by clearLocalDatabase —
    // not just 'pending' — so the preview count must include 'failed' too.
    setPendingVisitsCount((visitCounts.pending || 0) + (visitCounts.failed || 0))
    setResetDialogOpen(true)
  }, [])

  const confirmReset = useCallback(async () => {
    setResetting(true)
    try {
      await clearLocalDatabase()
      await refresh()
    } finally {
      setResetting(false)
      setResetDialogOpen(false)
    }
  }, [refresh])

  const checkpointsByEntity = Object.fromEntries(syncMeta.map(m => [m.entity, m.lastCheckpoint]))
  const bug01Active = enabledVisitHost && storeCounts && (storeCounts.employees ?? 0) === 0
  const bug02Active = storeCounts && !('comes' in checkpointsByEntity)

  return (
    <Stack spacing={3} className="audit-section">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <h3>{t('audit_indexeddb_title')}</h3>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={refresh} disabled={loading}>
            {loading ? t('audit_refreshing') : t('audit_refresh')}
          </Button>
          <Button variant="outlined" color="error" onClick={openResetDialog}>
            {t('audit_reset_db')}
          </Button>
        </Stack>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('audit_store_name')}</TableCell>
            <TableCell align="right">{t('audit_store_count')}</TableCell>
            <TableCell>{t('audit_store_status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {DEXIE_STORE_NAMES.map(name => {
            const count = storeCounts?.[name]
            const isEmpty = (count ?? 0) === 0
            const flagAsWarning = isEmpty && EXPECT_NON_EMPTY.includes(name)
            return (
              <TableRow key={name}>
                <TableCell>{name}</TableCell>
                <TableCell align="right">{count ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={flagAsWarning ? t('audit_status_empty_unexpected') : t('audit_status_ok')}
                    color={flagAsWarning ? 'warning' : 'success'}
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <div>
        <Typography variant="subtitle2">{t('audit_sync_meta_title')}</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('audit_sync_meta_entity')}</TableCell>
              <TableCell>{t('audit_sync_meta_checkpoint')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {syncMeta.map(m => (
              <TableRow key={m.entity}>
                <TableCell>{m.entity}</TableCell>
                <TableCell>{m.lastCheckpoint}</TableCell>
              </TableRow>
            ))}
            {syncMeta.length === 0 && (
              <TableRow>
                <TableCell colSpan={2}>{t('audit_no_data')}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {bug01Active && (
        <Chip
          color="error"
          variant="outlined"
          label={t('audit_bug01_warning')}
          sx={{ alignSelf: 'flex-start' }}
        />
      )}
      {bug02Active && (
        <Chip
          color="default"
          variant="outlined"
          label={t('audit_bug02_note')}
          sx={{ alignSelf: 'flex-start' }}
        />
      )}

      <Dialog open={resetDialogOpen} onClose={() => !resetting && setResetDialogOpen(false)}>
        <DialogTitle>{t('audit_reset_db_confirm_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('audit_reset_db_confirm_body')}</DialogContentText>
          {pendingVisitsCount > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('audit_reset_db_pending_warning', { count: pendingVisitsCount })}
            </Alert>
          )}
          <DialogContentText sx={{ mt: 2 }}>{t('audit_reset_db_next_step')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)} disabled={resetting}>
            {t('cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={confirmReset} disabled={resetting}>
            {resetting ? t('audit_resetting_db') : t('audit_reset_db_confirm_button')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

const mapStateToProps = state => ({
  enabledVisitHost: state.profile.enabled_visit_host,
})

export default connect(mapStateToProps)(IndexedDbHealthPanel)
